import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslationLang } from '@recipes/recipes-translation/recipes-translation-section/recipes-translation-section.component';
import { MatChipsModule } from '@angular/material/chips';
import { EditRecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { catchError, takeUntil } from 'rxjs/operators';
import { RefDataService } from '@services/ref-data.service';
import { forkJoin, of, Subject } from 'rxjs';
import { DataRef } from '@shared/models/dataref.model';
import { TranslateModule } from '@ngx-translate/core';
import { NgFor, NgIf, UpperCasePipe } from '@angular/common';
import { ResourceMediaLite } from '@shared/models/generic/media/resource-media-lite.model';
import { RecipesTranslationHeaderActionsComponent } from '@recipes/recipes-translation/recipes-translation-header-actions/recipes-translation-header-actions.component';

@Component({
  selector: 'app-recipes-translation-header',
  templateUrl: './recipes-translation-header.component.html',
  styleUrls: ['./recipes-translation-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatChipsModule, TranslateModule, NgIf, NgFor, UpperCasePipe, RecipesTranslationHeaderActionsComponent],
})
export class RecipesTranslationHeaderComponent implements OnInit, OnDestroy {
  @Input() translationLangs: TranslationLang;
  @Input() formTitle: string;
  @Input() recipe: EditRecipeLite;
  @Input() buttonsDisabled: boolean;
  @Output() translationApproved = new EventEmitter<boolean>();

  hasCover = false;
  coverToDisplay: string;
  domainName: string;
  marketName: string;
  langName: string;
  courseTypesNameList: string[] = [];
  destroy$ = new Subject<void>();

  constructor(
    private readonly _refDataService: RefDataService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.fetchDatas();
    this.initCover();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getName(key: string, list: DataRef[]): string {
    const obj: DataRef | undefined = (list || []).find((object: DataRef) => object.key === key);
    return obj?.translatedName ?? '';
  }

  containsCover(resourceMedias: ResourceMediaLite[]): boolean {
    return !!resourceMedias && resourceMedias.filter((resourceMedia) => resourceMedia.isCover).length === 1;
  }

  getCover(resourceMedias: ResourceMediaLite[]): string {
    const resourceMedia = resourceMedias?.find((rm) => rm.isCover);
    return resourceMedia?.media?.thumbnail ?? '';
  }

  onTranslationApproved(approveTranslation: boolean): void {
    this.translationApproved.emit(approveTranslation);
  }

  private fetchDatas(): void {
    //Get all datas and display it at the same time
    forkJoin({
      domains: this._refDataService.getByKey('domains', true).pipe(catchError(() => of(null))),
      markets: this._refDataService.getByKey('markets', true).pipe(catchError(() => of(null))),
      languages: this._refDataService.getByKey('languages', true).pipe(catchError(() => of(null))),
      courseTypes: this._refDataService.getByKey('courseTypes', true).pipe(catchError(() => of(null))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ domains, markets, languages, courseTypes }) => {
        //Get domain name
        const domainsList: DataRef[] = this._refDataService.translateReference(this._refDataService.toArray(domains));
        this.domainName = this.getName(this.recipe.domain, domainsList);

        //Get market name
        const marketsList: DataRef[] = this._refDataService.translateReference(this._refDataService.toArray(markets));
        this.marketName = this.getName(this.recipe.market, marketsList);

        //Get language name
        const languagesList: DataRef[] = this._refDataService.translateReference(this._refDataService.toArray(languages));
        this.langName = this.getName(this.recipe.lang, languagesList);

        //Get course types names
        const courseTypesList = this._refDataService.translateReference(this._refDataService.toArray(courseTypes));
        this.recipe.courses?.forEach((courseType: string) => {
          this.courseTypesNameList.push(this.getName(courseType, courseTypesList));
        });

        this._changeDetectorRef.detectChanges();
      });
  }

  private initCover(): void {
    this.coverToDisplay = this.getCover(this.recipe.resourceMedias);
    this.hasCover = this.containsCover(this.recipe.resourceMedias);
  }
}
